import type { ManualTaskType, RecordEntity } from '@crm/contracts';

/**
 * What the account manager has typed for one meeting and not saved yet, kept in this browser: the
 * window can be closed, the tab reloaded or another meeting opened in between, and nothing typed
 * is lost. Keyed by the meeting id, so reopening the same meeting resumes it.
 *
 * The note and the tasks are saved separately, each by its own button: the note on the record the
 * AM picks under it, a task straight away on the client's PROJECT (the record page that shows
 * tasks; contacts, deals and jobs do not). So the draft holds the note and the ONE task being
 * written, never a queue of unsaved tasks.
 */
export interface TaskDraft {
  title: string;
  /** The assignee; null = whoever saves it (the API's default). */
  assigneeId: string | null;
  /** A day (`YYYY-MM-DD`), or null for no due date. */
  due: string | null;
  type: ManualTaskType;
}

export type Target = { entityType: RecordEntity; entityId: string };

export interface MeetingDraft {
  notes: string;
  task: TaskDraft;
  /** The project the AM picked (else the one found from the attendees). */
  projectId: string | null;
  /** Where the note goes (else the project itself). */
  noteTarget: Target | null;
}

export const blankTask = (): TaskDraft => ({
  title: '',
  assigneeId: null,
  due: null,
  type: 'follow_up',
});

export const emptyDraft = (): MeetingDraft => ({
  notes: '',
  task: blankTask(),
  projectId: null,
  noteTarget: null,
});

const keyOf = (meetingId: string) => `crm:meeting-companion:draft:${meetingId}`;

export function readDraft(meetingId: string): MeetingDraft {
  try {
    const raw = window.localStorage.getItem(keyOf(meetingId));
    if (raw) {
      const stored = JSON.parse(raw) as Partial<MeetingDraft>;
      return { ...emptyDraft(), ...stored, task: { ...blankTask(), ...stored.task } };
    }
  } catch (err) {
    console.warn('meeting companion: draft read failed, starting empty', err);
  }
  return emptyDraft();
}

export function writeDraft(meetingId: string, draft: MeetingDraft): void {
  try {
    window.localStorage.setItem(keyOf(meetingId), JSON.stringify(draft));
  } catch (err) {
    console.warn('meeting companion: draft write failed (it stays in the open window)', err);
  }
}
